import {
  Box3,
  Group,
  Matrix4,
  Object3D,
  OrthographicCamera,
  PerspectiveCamera,
  Scene,
  Sprite,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three';
import { ViewMode } from '@src/app/model/view-mode';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

export class RenderCameraHelper {
  public static readonly SCREENSHOT_CLEARING: number = 0.4;

  /**
   * Calculate the total bounding box of all objects in the scene, excluding sprites and background groups.
   * Intended to be used for camera fitting and scene bounding calculations.
   *
   * **Scene provided will not be modified.**
   */
  public static getTotalObjectBox(scene: Scene): Box3 {
    // Copy the scene
    const gridCopy = scene.clone(true);
    // Now remove all objects that do not add to the size (sprites and grid)
    // (sprites are the attachment points)
    const objectsToRemove: Object3D[] = [];
    gridCopy.traverse((o) => {
      // Add to array to remove later to avoid null pointer exceptions
      if ((o instanceof Sprite) || (o instanceof Group && (o.name === 'background'))) {
        objectsToRemove.push(o);
      }
    });
    // Now remove all of them
    objectsToRemove.forEach(o => o.removeFromParent());

    // Create a box around everything and return it
    return new Box3().setFromObject(gridCopy);
  }

  public static getConfigurationGroupFromScene(scene: Scene): Group {
    return scene.getObjectByName('configuration') as Group;
  }

  /**
   * Fit the camera to the configuration being displayed in the scene, appropriate to the view mode.
   *
   * For cleanFront and cleanSide, this ensures the camera is positioned at a right angle to the configuration,
   * with the longest side facing the camera.
   *
   * **Note that the provided camera, renderer, and controls will be modified in place!**
   *
   * @param scene - The scene containing the configuration group.
   * @param viewMode - The view mode to determine the camera position and orientation.
   * @param renderer - The WebGL renderer used to render the scene.
   * @param camera - The camera to be positioned and oriented.
   * @param controls - The orbit controls to be updated with the camera's target.
   * @param configurationGroup - The group containing the configuration to fit the camera to.
   * @param cameraDistanceOrthographic - How far away we put the camera in orthographic views. Note that this is fairly
   * arbitrary, as the ratios are what matter in orthographic views, not the actual distance, as long as we stay in the
   * cylinder and don't overlap the configuration.
   * @param idealBoxDimensions - The ideal box dimensions to fit the configuration into for the cleanNice view mode.
   */
  public static fitCameraToConfiguration(
    scene: Scene,
    viewMode: ViewMode,
    renderer: WebGLRenderer,
    camera: OrthographicCamera | PerspectiveCamera,
    controls: OrbitControls,
    configurationGroup: Group,
    cameraDistanceOrthographic: number,
    idealBoxDimensions: Vector3 = new Vector3(6, 3, 4),
  ): boolean | undefined {
    let isOrthographicCameraRotated: boolean | undefined;

    // Determine model center, sizes and ratios
    const totalBox = RenderCameraHelper.getTotalObjectBox(scene);
    let totalBoxCenter = new Vector3();
    totalBox.getCenter(totalBoxCenter);
    let totalBoxSize = new Vector3();
    totalBox.getSize(totalBoxSize);
    const renderSize = new Vector2();
    renderer.getSize(renderSize);
    const screenRatio = renderSize.x / renderSize.y;

    switch (viewMode) {
      case ViewMode.cleanTop:
        if (!(camera instanceof OrthographicCamera)) {
          throw new Error('Camera must be an instance of OrthographicCamera for cleanTop view mode');
        }
        camera.position.set(totalBoxCenter.x, totalBoxCenter.y + cameraDistanceOrthographic, totalBoxCenter.z);
        camera.lookAt(totalBoxCenter);
        const curRot = camera.rotation;

        if (totalBoxSize.x > totalBoxSize.z) {
          isOrthographicCameraRotated = false;
          RenderCameraHelper._setOrthographicCameraWindow(
            totalBoxSize.z,
            totalBoxSize.x,
            screenRatio,
            RenderCameraHelper.SCREENSHOT_CLEARING,
            camera
          );
        } else {
          camera.rotation.set(curRot.x, curRot.y, curRot.z + (Math.PI / 2));
          isOrthographicCameraRotated = true;
          RenderCameraHelper._setOrthographicCameraWindow(
            totalBoxSize.x,
            totalBoxSize.z,
            screenRatio,
            RenderCameraHelper.SCREENSHOT_CLEARING,
            camera
          );
        }
        break;

      case ViewMode.cleanFront:
        if (!(camera instanceof OrthographicCamera)) {
          throw new Error('Camera must be an instance of OrthographicCamera for cleanFront view mode');
        }
        if (totalBoxSize.x > totalBoxSize.z) {
          isOrthographicCameraRotated = true;
          camera.position.set(totalBoxCenter.x, totalBoxCenter.y, totalBoxCenter.z + cameraDistanceOrthographic);
          camera.lookAt(totalBoxCenter);
          RenderCameraHelper._setOrthographicCameraWindow(
            totalBoxSize.y,
            totalBoxSize.x,
            screenRatio,
            RenderCameraHelper.SCREENSHOT_CLEARING,
            camera
          );
        } else {
          isOrthographicCameraRotated = false;
          camera.position.set(totalBoxCenter.x + cameraDistanceOrthographic, totalBoxCenter.y, totalBoxCenter.z);
          camera.lookAt(totalBoxCenter);
          RenderCameraHelper._setOrthographicCameraWindow(
            totalBoxSize.y,
            totalBoxSize.z,
            screenRatio,
            RenderCameraHelper.SCREENSHOT_CLEARING,
            camera
          );
        }
        break;

      case ViewMode.cleanSide:
        if (!(camera instanceof OrthographicCamera)) {
          throw new Error('Camera must be an instance of OrthographicCamera for cleanSide view mode');
        }
        if (totalBoxSize.x > totalBoxSize.z) {
          isOrthographicCameraRotated = true;
          camera.position.set(totalBoxCenter.x - cameraDistanceOrthographic, totalBoxCenter.y, totalBoxCenter.z);
          camera.lookAt(totalBoxCenter);
          RenderCameraHelper._setOrthographicCameraWindow(
            totalBoxSize.y,
            totalBoxSize.x,
            screenRatio,
            RenderCameraHelper.SCREENSHOT_CLEARING,
            camera
          );
        } else {
          isOrthographicCameraRotated = false;
          camera.position.set(totalBoxCenter.x, totalBoxCenter.y, totalBoxCenter.z + cameraDistanceOrthographic);
          camera.lookAt(totalBoxCenter);
          // todo: is this correct? Shouldn't it be totalBoxSize.z?
          RenderCameraHelper._setOrthographicCameraWindow(
            totalBoxSize.y,
            totalBoxSize.x,
            screenRatio,
            RenderCameraHelper.SCREENSHOT_CLEARING,
            camera
          );
        }
        break;

      case ViewMode.cleanNice:
        if (!(camera instanceof PerspectiveCamera)) {
          throw new Error('Camera must be an instance of PerspectiveCamera for cleanNice view mode');
        }
        // If the config is longer than it is wide we rotate it 90 degrees to fit better
        if (totalBoxSize.x < totalBoxSize.z) {
          configurationGroup.rotateY(Math.PI / 2);

          // Reset size and center because the supplied totalBox will be wrong now
          const newTotalBox = RenderCameraHelper.getTotalObjectBox(scene);
          totalBoxCenter = new Vector3();
          newTotalBox.getCenter(totalBoxCenter);
          totalBoxSize = new Vector3();
          newTotalBox.getSize(totalBoxSize);
        }

        // Scale the configuration so that it fits into a predefined ideal box
        const ratios = [
          totalBoxSize.x / idealBoxDimensions.x,
          totalBoxSize.y / idealBoxDimensions.y,
          totalBoxSize.z / idealBoxDimensions.z,
        ];
        const maxRatio = Math.max(...ratios);
        const scaleFactor = 1 / maxRatio;

        // Calculate the config center after scaling to use later
        const configCenterAfterScaling = new Vector3(
          totalBoxCenter.x * scaleFactor,
          idealBoxDimensions.y * scaleFactor,
          totalBoxCenter.z * scaleFactor
        );

        const scaleMatrix = new Matrix4().makeScale(scaleFactor, scaleFactor, scaleFactor);
        configurationGroup.applyMatrix4(scaleMatrix);

        // Draw box for the ideal box we want to fit the config into, to be uncommented for debugging purposes
        // const idealBoxGeometry = new BoxGeometry(idealBoxDimensions.x, idealBoxDimensions.y, idealBoxDimensions.z);
        // const idealBoxMesh = new Mesh(idealBoxGeometry, this._debugService.generateMaterial(ColorType.Orange));
        //
        // idealBoxMesh.position.set(configCenterAfterScaling.x, idealBoxDimensions.y / 2, configCenterAfterScaling.z);
        // idealBoxMesh.userData = {[DEBUG]: 1}; // debug objects are not used in raytracing
        // this._backgroundGroup.add(idealBoxMesh);

        // Move config to the front of the ideal box to be as close as possible
        const offsetInBox = new Vector3(
          0,
          0,
          (idealBoxDimensions.z - (totalBoxSize.z * scaleFactor)) / 2,
        );
        configurationGroup.position.add(offsetInBox);

        // Position the camera so that it looks directly at the box
        camera.position
          .set(configCenterAfterScaling.x, 0, configCenterAfterScaling.z)
          .add(new Vector3(4.4, 1.5, 5.4));
        controls.target.set(configCenterAfterScaling.x, 1.25, configCenterAfterScaling.z);
        break;
      default:
        break;
    }

    camera.updateProjectionMatrix();
    controls.update();

    return isOrthographicCameraRotated;
  }

  /**
   * Helper to determine orthographic camera position and in-place modify top/bottom/left/right.
   */
  private static _setOrthographicCameraWindow(
    sizeTopBot: number,
    sizeLR: number,
    ratio: number,
    clearing: number,
    camera: OrthographicCamera
  ): OrthographicCamera {
    // Determine the ratio of the box size for this view (length / height) and the corresponding multiplier
    const boxRatioSide = sizeLR / sizeTopBot;
    const multiplierSide = boxRatioSide / ratio;

    // if the screen 'taller' than the configuration, make the camera angle wider from top to bottom
    // if the screen is 'smaller' than the configuration, make the camera angle wider from left to right
    camera.top = clearing + Math.max(1, multiplierSide) * sizeTopBot / 2;
    camera.bottom = -(clearing + Math.max(1, multiplierSide) * sizeTopBot / 2);
    camera.left = -(clearing + sizeLR / (2 * Math.min(1, multiplierSide)));
    camera.right = clearing + sizeLR / (2 * Math.min(1, multiplierSide));
    return camera;
  }
}
